import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { UseMutationOptions, useQueryClient } from 'react-query';
import http from '../config/http-service';
import type { GpoAwardConfirmationMeta, RestaurantInquiryItem, RestaurantInquiryQuote } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// POST .../quotes/:quoteId/accept — accepts the quote AND, in one transaction,
// auto-rejects every other PENDING quote on the item (metadata.lostToQuoteId)
// and flips the item to FULFILLED. `reason` is REQUIRED by the DTO — this is a
// deliberate accountability choice, do not relax it client-side. Returns the
// updated ITEM (with nested quotes[]), not the quote.
type AcceptVariables = { id: number | string; itemId: number; quoteId: number; reason: string };

// On an APP item, awarding also confirms the price to the restaurant's draft order (GPO request) —
// meta.gpoConfirmation carries that confirmation for the UI to surface. Absent on a CMS item, or on
// a pre-deploy backend that doesn't send it yet.
export interface AcceptQuoteResult {
  data: RestaurantInquiryItem;
  meta?: { gpoConfirmation?: GpoAwardConfirmationMeta | null };
}

// POST .../quotes/:quoteId/reject — declines one PENDING quote; nothing else
// changes (other quotes and the item keep their status). `reason` optional.
// Returns the quote, now REJECTED.
type RejectVariables = { id: number | string; itemId: number; quoteId: number; reason?: string };

// POST .../quotes/:quoteId/revert — the undo for accept. In one transaction the
// ACCEPTED quote returns to PENDING, the quotes that lost to it are restored to
// PENDING, and the item drops out of FULFILLED back to QUOTED, so the item can
// be re-awarded to anyone. `reason` is REQUIRED, the same accountability rule
// as accept.
//
// 409 once the quote carries an orderId: a real order exists downstream and the
// LPO has already gone out, so there is nothing left to revert.
//
// A losing quote is only restored when its product has no live quote again (the
// partial uq_riq_item_product index forbids two on one product) — hence
// meta.skippedQuotes, which callers must SURFACE rather than swallow: a nonzero
// count means the item did not fully return to its pre-award shape.
//
// Unlike the two hooks above this returns the whole envelope, not response.data
// — the counts live in meta and would be lost by unwrapping.
type RevertVariables = { id: number | string; itemId: number; quoteId: number; reason: string };

export interface RevertQuoteResult {
  data: RestaurantInquiryItem;
  meta: { restoredQuotes: number; skippedQuotes: number };
}

type TError = AxiosError;

export function useAcceptInquiryItemQuoteApi(
  options?: Omit<UseMutationOptions<AcceptQuoteResult, TError, AcceptVariables>, 'mutationFn'>
) {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, itemId, quoteId, reason }: AcceptVariables): Promise<AcceptQuoteResult> => {
    const response = await http.post(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}/quotes/${quoteId}/accept`, {
      reason,
    });
    // meta is absent on a pre-deploy backend / a CMS item; default it rather than let the
    // caller read undefined.
    const body = response.data ?? {};
    return { data: body.data ?? body, meta: body.meta };
  };

  return useMutationWrapper<AcceptQuoteResult, TError, AcceptVariables>(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'accept-quote'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}

export function useRejectInquiryItemQuoteApi(
  options?: Omit<UseMutationOptions<RestaurantInquiryQuote, TError, RejectVariables>, 'mutationFn'>
) {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, itemId, quoteId, reason }: RejectVariables): Promise<RestaurantInquiryQuote> => {
    const response = await http.post(
      `${restaurantInquiriesEndpoint}/${id}/items/${itemId}/quotes/${quoteId}/reject`,
      reason ? { reason } : {}
    );
    return response.data?.data ?? response.data;
  };

  return useMutationWrapper<RestaurantInquiryQuote, TError, RejectVariables>(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'reject-quote'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}

export function useRevertInquiryItemQuoteApi(
  options?: Omit<UseMutationOptions<RevertQuoteResult, TError, RevertVariables>, 'mutationFn'>
) {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...restOptions } = options || {};

  const mutationFn = async ({ id, itemId, quoteId, reason }: RevertVariables): Promise<RevertQuoteResult> => {
    const response = await http.post(`${restaurantInquiriesEndpoint}/${id}/items/${itemId}/quotes/${quoteId}/revert`, {
      reason,
    });
    // meta is absent on a pre-deploy backend; default it rather than let the
    // caller read undefined counts.
    const body = response.data ?? {};
    return { data: body.data ?? body, meta: body.meta ?? { restoredQuotes: 0, skippedQuotes: 0 } };
  };

  return useMutationWrapper<RevertQuoteResult, TError, RevertVariables>(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'revert-quote'],
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      userOnSuccess?.(data, variables, context);
    },
    ...restOptions,
  });
}
